<script lang="ts" setup>
export interface A11yDialogProps {
  id: string
  isDangerousAction?: boolean
  onConfirm: Function
  confirmText?: string
  cancelText?: string
}
import { ref } from 'vue'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const dialog = ref(null)

const emit = defineEmits(['dialog'])

const setDialogRef = (d) => {
  if (d) {
    d.on('show', () => {
      disableBodyScroll(d)
    })
    d.on('hide', () => {
      enableBodyScroll(d)
    })
    d.on('destroy', () => {
      clearAllBodyScrollLocks()
    })
    dialog.value = d
    emit('dialog', dialog)
  }
}

const props = defineProps<A11yDialogProps>()
</script>

<template>
  <a11y-dialog
    :id="props.id"
    :class-names="{
      container: 'dialog-container',
      overlay: 'dialog-overlay',
      document: 'dialog-content',
      title: 'dialog-title',
      closeButton: 'dialog-close idsk-button idsk-button--secondary',
    }"
    close-button-label="t('globalne.zatvoritOkno')"
    @dialog-ref="setDialogRef"
  >
    <template #title>
      <IDSKHeading as="h2" variant="m" class="mb-3">
        <slot name="title">Title</slot>
      </IDSKHeading>
    </template>
    <IDSKParagraph class="dialog-body">
      <slot name="content">Content</slot>
    </IDSKParagraph>
    <IDSKButtonGroup class="dialog-actions mb-0">
      <slot name="actions">
        <IDSKButton
          data-test-id="A11-dialog-confirm"
          data-a11y-dialog-hide
          :variant="isDangerousAction ? 'warning' : 'primary'"
          @click="props.onConfirm"
        >
          {{ confirmText ?? t('globalne.potvrdit') }}
        </IDSKButton>
        <IDSKButton
          variant="secondary"
          data-a11y-dialog-hide
          data-test-id="A11-dialog-cancel"
        >
          {{ cancelText ?? t('globalne.zrusit') }}
        </IDSKButton>
      </slot>
    </IDSKButtonGroup>
  </a11y-dialog>
</template>

<style lang="scss">
.dialog {
  &-close {
    width: 40px;
    margin-bottom: 0;
    position: absolute;
    right: 15px;
    &:active {
      top: 17px;
    }
  }

  &-title {
    text-align: center;
    padding-left: 55px;
    padding-right: 55px;
  }

  &-body {
    text-align: center;
  }

  &-actions {
    margin-right: 0;
    justify-content: center;

    & > .idsk-button:last-child {
      margin-right: 0 !important;
    }
  }

  &-container {
    display: flex;
    z-index: 10000;
  }

  &-overlay {
    background-color: rgba(43, 46, 56, 0.9);
    animation: fade-in 200ms both;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
  }

  &-overlay,
  &-container {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  &-container[aria-hidden='true'] {
    display: none;
  }

  &-content {
    background-color: var(--idsk-color-white);
    margin: auto;
    z-index: 2;
    position: relative;
    padding: 1em;
    max-width: 90%;
    width: 600px;
    border-radius: 5px 5px 2px 2px;
  }
}
</style>
